import { FC, PropsWithChildren, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/_atoms/dropdown-menu";
import { IconPen } from "@/components/_icons/IconPen";
import { IconTrash } from "@/components/_icons/IconTrash";
import { DeleteConfirmation } from "@/components/_molecules/delete-confirmation";
import { useGetLabelGroup } from "@/domain/label/hooks/api/use-get-label-group";
import { useWorkspaceTerminology } from "@/domain/workspace/hooks/use-workspace-terminology";

import { DeleteLabelGroupAction } from "./actions/delete-label-group-action";

export interface LabelGroupActionButtonProps extends PropsWithChildren {
  labelGroup: {
    id: string;
  };
  workspace: {
    id: string;
  };
  handleEditLabelGroup: () => void;
  asChild?: boolean;
}

export const LabelGroupActionButton: FC<LabelGroupActionButtonProps> = ({
  workspace,
  handleEditLabelGroup,
  asChild,
  children,
  ...props
}) => {
  const { t } = useTranslation("common");
  const terminology = useWorkspaceTerminology(workspace.id);
  const [open, setOpen] = useState(false);
  const [deleteConfirmationisOpen, setDeleteConfirmationisOpen] = useState(false);
  const [triggerDelete, setTriggerDelete] = useState(false);
  const { labelGroup } = useGetLabelGroup({ id: props.labelGroup.id });

  if (!labelGroup) {
    return <></>;
  }

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild={asChild}>{children}</DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            className="cursor-pointer text-foreground/60 hover:bg-accent hover:text-accent-foreground"
            aria-selected
            onClick={handleEditLabelGroup}
            aria-label={t("label.editGroup")}
          >
            <div className="relative flex items-center overflow-hidden text-ellipsis">
              <IconPen className="mr-2 h-3 w-3" />
              {t("label.editGroup")}
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem
            className="cursor-pointer text-foreground/60 hover:bg-accent hover:text-accent-foreground"
            aria-selected
            onClick={() => {
              setDeleteConfirmationisOpen(true);
            }}
            aria-label={t("label.deleteGroup")}
          >
            <div className="relative flex items-center overflow-hidden text-ellipsis">
              <IconTrash className="mr-2 h-3 w-3" />
              {t("label.deleteGroup")}
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteLabelGroupAction asChild labelGroup={labelGroup} workspace={workspace} forceDelete={triggerDelete}>
        <DeleteConfirmation
          open={deleteConfirmationisOpen}
          onOpenChange={setDeleteConfirmationisOpen}
          onConfirm={() => {
            setTriggerDelete(true);
          }}
          title={t("label.deleteGroupConfirmTitle")}
          description={`Diese Aktion kann nicht rückgängig gemacht werden. Die Gruppe und alle damit verbundenen Labels werden unwideruflich gelöscht und von allen ${terminology.climateAction.plural} entfernt.`}
        />
      </DeleteLabelGroupAction>
    </>
  );
};
